import React, { useState, } from 'react';
import { useNotify } from 'react-admin';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, InputBase
} from '@material-ui/core';
import { 
  VisibilityOffOutlined, VisibilityOutlined, HighlightOffOutlined,
  Search as SearchIcon
} from '@material-ui/icons';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import { reqSearchMenus, reqGetFilteredMenu, reqPostFilteredMenu } from './api';

import styles from './FilterMenuDialog.module.css';

const PER_PAGE = 30;

const FilterMenuDialog = ({ customerId, open, onClose }) => {

  const notify = useNotify();

  const [ filtered, setFiltered ] = useState([]);
  const [ loadingGetFiltered, setLoadingGetFiltered ] = useState(false);
  const [ loadingPostFiltered, setLoadingPostFiltered ] = useState(false);


  const handleEnter = async () => {

    try {
      setLoadingGetFiltered(true);
      const { data } = await reqGetFilteredMenu(customerId);
      setFiltered(data);
      setLoadingGetFiltered(false);
    } catch (err) {
      console.error(err);
      setLoadingGetFiltered(false);
      notify(`제외된 메뉴 목록을 불러오지 못했습니다.\n오류:${err}`, 'error');
    }
  }

  const handleAdd = (menu, index) => {
    setFiltered(filtered.concat([ menu ]));
  }

  const handleRemove = (menu) => {
    setFiltered(filtered.filter(el => el.menuId !== menu.menuId));
  }
  
  const handleClose = () => {
    onClose();
  }

  const handleApply = async () => {
    try {
      setLoadingPostFiltered(true);
      const { data } = await reqPostFilteredMenu(
        customerId, 
        filtered.map(el => ({ supplierId: el.supplierId, menuId: el.menuId }))
      );
      // TODO check result 
      onClose();
      setLoadingPostFiltered(false);
    } catch (err) {
      console.error(err);
      setLoadingPostFiltered(false);
      notify(`제외된 메뉴 목록 저장에 실패했습니다.\n오류:${err}`, 'error');
    }
  }

  return (
    <Dialog
      open={open}
      onEnter={handleEnter}
      onClose={handleClose}
    >
      <DialogTitle>
        메뉴 필터링
        <small style={{ fontSize: '14px', marginLeft: '20px' }}>
          제외된 메뉴는 큐레이션되지 않습니다.
        </small>
      </DialogTitle>
      <DialogContent>
        <div className={styles.body}>
          <div className={styles.listContainer}>
            <AllMenus 
              filtered={filtered}
              onAdd={handleAdd}
              onRemove={handleRemove}
            />
          </div>
          <div className={styles.listContainer}>
            <FilteredMenus
              loading={loadingGetFiltered}
              filtered={filtered}
              onRemove={handleRemove}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loadingPostFiltered}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          disabled={loadingPostFiltered}
          color="primary"
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const AllMenus = ({ filtered, onAdd, onRemove }) => {

  const [ viewModel, setViewModel ] = useState({ count: 1, search: '' });
  const [ items, setItems ] = useState([]);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState();

  const isItemLoaded = index => {
    return items.length === viewModel.count || index < items.length;
  }

  const loadMore = async (startIndex) => {
    if (loading || startIndex >= viewModel.count) return ; // do nothing

    try {
      setLoading(true);

      // load next page 
      const nextPage = Math.ceil(items.length / PER_PAGE) + 1;
      const { total, data } = 
        await reqSearchMenus(nextPage, PER_PAGE, viewModel.search);
      setItems(items.concat(data));
      setViewModel(Object.assign({}, viewModel, { count: total }));

      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      setError(err);
    }
  }

  const handleClickSearch = () => {
    setItems([]);
    setViewModel(Object.assign({}, viewModel, { count: 1 }));
  }

  const handleChangeSearch = evt => {
    setViewModel(Object.assign({}, viewModel, { search: evt.target.value }));
  }

  // renderers 
  const ListItem = ({ index, style }) => {
    if (!isItemLoaded(index)) {
      return (
        <div
          key={index}
          style={style}
          className={styles.item}
        >
            loading ...
        </div>
      );
    }

    const ftd = isFiltered(filtered, items[index]);
    return (
      <div
        key={index}
        style={style}
        className={styles.item}
      >
        <div className={styles.content}>
          <div className={styles.supplier}>{items[index].supplierName}</div>
          <div className={styles.menu}>{items[index].menuName}</div>
        </div>
        <div className={styles.action}>
          <IconButton
            style={{ padding: '8px' }}
            onClick={() => { ftd ? onRemove(items[index], index) : onAdd(items[index], index) }}
          >
            {ftd ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
          </IconButton>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.searchCtn}>
        <InputBase
          style={{ flex: '1 0' }}
          placeholder="메뉴명 검색"
          value={viewModel.search}
          onChange={handleChangeSearch}
        />
        <IconButton
          onClick={handleClickSearch}
        >
          <SearchIcon />
        </IconButton>
      </div>
      <div>
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          loadMoreItems={loadMore}
          itemCount={viewModel.count}
        >
          {({ onItemsRendered, ref }) => (
            <FixedSizeList
              width={250}
              height={400}
              itemSize={40}
              itemCount={viewModel.count}
              onItemsRendered={onItemsRendered}
              ref={ref}
            >
              {ListItem}
            </FixedSizeList>
          )}
        </InfiniteLoader>
      </div>
    </div>
  );
}

const FilteredMenus = ({ loading, filtered, onRemove }) => {

  return (
    <div>
      <div className={styles.listTitle}>
        제외된 메뉴
      </div>
      <div>
        {loading && 'loading'}
        <FixedSizeList
          width={250}
          height={400}
          itemSize={40}
          itemCount={filtered.length}
        >
          {({ index, style }) => (
            <div
              key={index}
              style={style}
              className={styles.item}
            >
              <div className={styles.content}>
                <div className={styles.supplier}>{filtered[index].supplierName}</div>
                <div className={styles.menu}>{filtered[index].menuName}</div>
              </div>
              <div className={styles.action}>
                <IconButton
                  style={{ padding: '8px' }}
                  onClick={() => { onRemove(filtered[index]) }}
                >
                  <HighlightOffOutlined />
                </IconButton>
              </div>
            </div>
          )}
        </FixedSizeList>
      </div>
    </div>
  )
}

const isFiltered = (filteredList, item) => {
  return filteredList.some(el => el.menuId === item.menuId);
}

export default FilterMenuDialog;